import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { Check, X, RefreshCw, ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadPayments = () => {
    setLoading(true);
    adminAPI.getPayments(statusFilter).then(r => { setPayments(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { loadPayments(); }, [statusFilter]); // eslint-disable-line

  const handlePayment = async (paymentId, action) => {
    setActionLoading(paymentId);
    try {
      await adminAPI.approvePayment(paymentId, action);
      toast.success(`Payment ${action}`);
      loadPayments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(null); }
  };

  const pendingCount = payments.length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-payments-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Payment Management</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Approve or reject user transactions</p>
          </div>
          <button onClick={loadPayments} data-testid="refresh-payments-btn" className="flex items-center gap-1.5 px-4 py-2 bg-green-500/15 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/25 transition-colors border border-green-500/20">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'pending', label: 'Pending', icon: Clock, color: 'amber' },
            { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'emerald' },
            { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'red' },
          ].map(f => {
            const Icon = f.icon;
            return (
              <button key={f.key} onClick={() => setStatusFilter(f.key)} data-testid={`admin-payment-filter-${f.key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors border ${
                  statusFilter === f.key
                    ? `bg-${f.color}-500/20 text-${f.color}-400 border-${f.color}-500/30`
                    : 'text-[var(--text-secondary)] bg-[var(--bg-surface)] border-[var(--border-subtle)]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {f.label}
              </button>
            );
          })}
        </div>

        {/* Payment Cards */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : payments.length === 0 ? (
          <div className="p-10 text-center bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg">
            <p className="text-sm text-[var(--text-secondary)]">No {statusFilter} payments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {payments.map(p => (
              <div key={p.id} className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg hover:border-green-500/20 transition-all duration-200" data-testid={`payment-card-${p.id}`}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${p.type === 'deposit' ? 'bg-emerald-500/15' : 'bg-red-500/15'}`}>
                      {p.type === 'deposit' ? <ArrowDownToLine className="w-5 h-5 text-emerald-400" /> : <ArrowUpFromLine className="w-5 h-5 text-red-400" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${p.type === 'deposit' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                          {p.type}
                        </span>
                        <span className="text-[10px] text-[var(--text-tertiary)] uppercase font-medium">{p.method}</span>
                      </div>
                      <p className="text-sm text-[var(--text-primary)] font-medium">{p.user_name || 'Unknown User'}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{p.user_email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="text-right flex-1 sm:flex-none">
                      <p className="text-lg font-bold font-mono text-[var(--text-primary)]">{formatCurrency(p.amount)}</p>
                      <p className="text-[10px] text-[var(--text-tertiary)]">{p.created_at ? new Date(p.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''}</p>
                    </div>
                    {statusFilter === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePayment(p.id, 'approved')}
                          disabled={actionLoading === p.id}
                          data-testid={`approve-payment-${p.id}`}
                          className="flex items-center gap-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                        >
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handlePayment(p.id, 'rejected')}
                          disabled={actionLoading === p.id}
                          data-testid={`reject-payment-${p.id}`}
                          className="flex items-center gap-1 px-4 py-2 bg-red-500/15 text-red-400 hover:bg-red-500/25 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 border border-red-500/20"
                        >
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    )}
                    {statusFilter !== 'pending' && (
                      <span className={`px-3 py-1 rounded-lg text-xs font-medium ${
                        p.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                      }`}>
                        {p.status}
                      </span>
                    )}
                  </div>
                </div>
                {p.upi_id && (
                  <div className="mt-3 pt-3 border-t border-[var(--border-subtle)]">
                    <p className="text-xs text-[var(--text-secondary)]">UPI ID: <span className="font-mono text-[var(--text-primary)]">{p.upi_id}</span></p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
